import React, { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../api/api";
import { AuthContext } from "../AuthContext";

import {
  Box,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";

import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { motion } from "framer-motion";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const isMobile = useMediaQuery("(max-width:900px)");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await api.post("/api/token/", {
        email,
        password,
      });

      localStorage.setItem("access", res.data.access);
      localStorage.setItem("refresh", res.data.refresh);

      const userRes = await api.get("/api/user-profile/", {
        headers: {
          Authorization: `Bearer ${res.data.access}`,
        },
      });

      const user = userRes.data;

      setUser(user);
      localStorage.setItem("user", JSON.stringify(user));

      Swal.fire({
        icon: "success",
        title: "Login Successful",
        text: "Welcome to Zuluwhiskey Shipping System",
        timer: 1500,
        showConfirmButton: false,
      });

      setTimeout(() => {
        if (user.user_type === "owner") {
          navigate("/owner-dashboard");
        } else if (user.user_type === "admin") {
          navigate("/admin-dashboard");
        } else if (user.user_type === "sales_agent") {
          navigate("/sales-dashboard");
        } else {
          navigate("/dashboard");
        }
      }, 1500);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: "Invalid email or password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
        background: "#ffffff",
      }}
    >
      {/* ==========================================
          LEFT BRANDING SECTION
      ========================================== */}

      <Box
        sx={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(135deg,#0d1b2a 0%,#1b263b 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 4, md: 8 },
          minHeight: { xs: "45vh", md: "100vh" },
        }}
      >
        {/* Deep Orange Bubble */}

        <motion.div
          animate={{
            y: [0, -25, 0],
            x: [0, 15, 0],
          }}
          transition={{
            duration: 14,
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            width: isMobile ? 180 : 280,
            height: isMobile ? 180 : 280,
            borderRadius: "50%",
            background: "rgba(255,111,0,.20)",
            top: "8%",
            left: "-80px",
          }}
        />

        {/* Blue Bubble */}

        <motion.div
          animate={{
            y: [0, 25, 0],
            x: [0, -15, 0],
          }}
          transition={{
            duration: 16,
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            width: isMobile ? 150 : 240,
            height: isMobile ? 150 : 240,
            borderRadius: "50%",
            background: "rgba(33,150,243,.18)",
            right: "-60px",
            bottom: "12%",
          }}
        />

        {/* Light Orange */}

        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            width: isMobile ? 100 : 160,
            height: isMobile ? 100 : 160,
            borderRadius: "50%",
            background: "rgba(255,183,77,.20)",
            left: "30%",
            bottom: "20%",
          }}
        />

        {/* Tin Gray */}

        <motion.div
          animate={{
            y: [0, 18, 0],
          }}
          transition={{
            duration: 13,
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            width: isMobile ? 90 : 120,
            height: isMobile ? 90 : 120,
            borderRadius: "50%",
            background: "rgba(144,164,174,.22)",
            right: "15%",
            top: "15%",
          }}
        />

        {/* Small Stars */}

        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              opacity: [0.2, 1, 0.2],
            }}
            transition={{
              duration: 3 + (i % 4),
              repeat: Infinity,
            }}
            style={{
              position: "absolute",
              width: 3,
              height: 3,
              borderRadius: "50%",
              background:
                i % 4 === 0
                  ? "#ff9800"
                  : i % 4 === 1
                  ? "#2196f3"
                  : i % 4 === 2
                  ? "#ffcc80"
                  : "#b0bec5",
              top: `${(i * 13) % 100}%`,
              left: `${(i * 17) % 100}%`,
            }}
          />
        ))}

        <motion.div
          initial={{
            opacity: 0,
            x: -60,
          }}
          animate={{
            opacity: 1,
            x: 0,
          }}
          transition={{
            duration: 0.7,
          }}
          style={{
            position: "relative",
            zIndex: 5,
            maxWidth: "600px",
          }}
        >
<Typography
  variant={isMobile ? "h4" : "h2"}
  fontWeight={900}
  gutterBottom
  sx={{
    lineHeight: 1.2,
    letterSpacing: "0.5px",
  }}
>
  <Box
    component="span"
    sx={{
      color: "#ff6f00",
      textShadow: "0 0 12px rgba(255,111,0,.35)",
    }}
  >
    Zulu
  </Box>

  <Box
    component="span"
    sx={{
      color: "#ffffff",
    }}
  >
    whiskey
  </Box>

  {" "}

  <Box
    component="span"
    sx={{
      color: "#ff6f00",
      textShadow: "0 0 12px rgba(255,111,0,.35)",
    }}
  >
    Shipping
  </Box>
</Typography>

          <Typography
            sx={{
              color: "#d6d6d6",
              lineHeight: 1.8,
              fontSize: {
                xs: "1rem",
                md: "1.15rem",
              },
            }}
          >
            Fast • Secure • Reliable Logistics for Cars,
            Containers, Equipment, and Freight Shipping
            Worldwide.
          </Typography>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {[
              "🚛 Barrier / Equipment Transport",
              "🚢 Container Shipping",
              "🚗 Vehicle Delivery",
            ].map((item) => (
              <Box
                key={item}
                sx={{
                  background:
                    "rgba(255,255,255,.08)",
                  backdropFilter: "blur(10px)",
                  border:
                    "1px solid rgba(255,255,255,.08)",
                  p: 2,
                  borderRadius: 3,
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        </motion.div>
      </Box>

      {/* ==========================================
          RIGHT LOGIN SECTION
      ========================================== */}

      <Box
        sx={{
          flex: 1,
          background: "#f5f7fa",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 3, md: 5 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Top Right Bubble */}

        <motion.div
          animate={{
            y: [0, -20, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            width: isMobile ? 120 : 220,
            height: isMobile ? 120 : 220,
            borderRadius: "50%",
            background:
              "rgba(255,111,0,.12)",
            top: "-60px",
            right: "-60px",
          }}
        />

        {/* Bottom Left Bubble */}

        <motion.div
          animate={{
            y: [0, 25, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
          }}
          style={{
            position: "absolute",
            width: isMobile ? 120 : 180,
            height: isMobile ? 120 : 180,
            borderRadius: "50%",
            background:
              "rgba(33,150,243,.12)",
            bottom: "-50px",
            left: "-50px",
          }}
        />

        <motion.div
          initial={{
            opacity: 0,
            x: 60,
          }}
          animate={{
            opacity: 1,
            x: 0,
          }}
          transition={{
            duration: 0.7,
          }}
          style={{
            width: "100%",
            maxWidth: 450,
            zIndex: 5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: "24px",
              background: "#fff",
              boxShadow:
                "0 20px 50px rgba(0,0,0,.08)",
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              gutterBottom
            >
              Sign In
            </Typography>

            <Typography
              sx={{
                color: "#666",
                mb: 3,
              }}
            >
              Access your Zuluwhiskey Shipping
              dashboard
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                variant="filled"
                margin="normal"
                value={email}
                onChange={(e) =>
                  setEmail(e.target.value)
                }
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                variant="filled"
                margin="normal"
                value={password}
                onChange={(e) =>
                  setPassword(e.target.value)
                }
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword(
                            !showPassword
                          )
                        }
                      >
                        {showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 1,
                  mb: 3,
                }}
              >
                <Link
                  to="/forgotPassword"
                  style={{
                    color: "#ff8c00",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  py: 1.6,
                  borderRadius: "14px",
                  fontWeight: 700,
                  background:
                    "linear-gradient(135deg,#ff9800,#ffb300)",
                  color: "#fff",

                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#fb8c00,#ffa000)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={22}
                    sx={{
                      color: "#fff",
                    }}
                  />
                ) : (
                  "Login"
                )}
              </Button>
            </form>

            <Typography
              sx={{
                mt: 3,
                textAlign: "center",
                color: "#777",
                fontSize: ".9rem",
              }}
            >
              🚢 Fast • Secure • Reliable Shipping
              Solutions
            </Typography>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Login;

